/* Brutalist Contact — terminal-style form. `$ studio --contact`
   prompts. Monospace everything. Hard amber send action. */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function BrutalistContact() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "STUDIO";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => { document.title = `${studioName} // CONTACT`; }, [studioName]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Missing required fields."); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("// transmission received. response queued.");
    } catch (err) { toast.error(err?.response?.data?.detail || "// transmission failed."); }
    finally { setSending(false); }
  }

  const Field = ({ name, label, type = "text" }) => (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.32em] mb-2" style={{ color: "var(--tenant-fg-soft)" }}>
        &gt; {label}
      </label>
      <input type={type} value={form[name]}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        data-testid={`brutalist-contact-${name}`}
        className="w-full bg-transparent border-2 px-3 py-2 outline-none focus:border-current"
        style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)", color: "var(--tenant-fg)", fontFamily: "var(--tenant-sans)" }} />
    </div>
  );

  return (
    <div data-testid="brutalist-contact"
      style={{ background: "var(--tenant-bg, #0C0A09)", color: "var(--tenant-fg, #FAFAF8)", fontFamily: "var(--tenant-sans, monospace)" }}>

      <section className="px-6 lg:px-12 pt-16 pb-10">
        <div className="max-w-[1480px] mx-auto">
          <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>// CONTACT.SH</div>
          <div className="flex items-center gap-6 mt-4 mb-12 pb-4 border-b" style={{ borderColor: "var(--tenant-accent)" }}>
            <h1 className="leading-[0.85] uppercase" style={{ fontFamily: "var(--tenant-serif, monospace)", fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
              TRANSMIT
            </h1>
            <div className="flex-1 h-px" style={{ background: "var(--tenant-accent)" }} />
          </div>
        </div>
      </section>

      <div className="max-w-[1480px] mx-auto px-6 lg:px-12 pb-32 grid grid-cols-1 md:grid-cols-12 gap-8">
        <aside className="md:col-span-4 text-[10px] uppercase tracking-[0.32em] space-y-2" style={{ color: "var(--tenant-fg-soft)" }}>
          <div>$ studio --info</div>
          <div className="opacity-50">─────────────</div>
          {settings?.contact_location && <div>[LOC]   : {settings.contact_location}</div>}
          {settings?.contact_email && <div>[EMAIL] : <a href={`mailto:${settings.contact_email}`} className="underline" style={{ color: "var(--tenant-accent)" }}>{settings.contact_email}</a></div>}
          {settings?.contact_phone && <div>[PHONE] : <a href={`tel:${settings.contact_phone}`} className="underline" style={{ color: "var(--tenant-accent)" }}>{settings.contact_phone}</a></div>}
          {settings?.instagram_url && <div>[IG]    : <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "var(--tenant-accent)" }}>active</a></div>}
          <div className="pt-4">[SLA]   : &lt; 48 HOURS</div>
        </aside>

        <form onSubmit={onSubmit} className="md:col-span-8 space-y-6">
          <Field name="name" label="NAME []" />
          <Field name="email" label="EMAIL []" type="email" />
          <Field name="phone" label="PHONE [opt]" type="tel" />
          <div>
            <label className="block text-[10px] uppercase tracking-[0.32em] mb-2" style={{ color: "var(--tenant-fg-soft)" }}>&gt; BRIEF []</label>
            <textarea rows={6} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="brutalist-contact-message"
              className="w-full bg-transparent border-2 px-3 py-2 outline-none focus:border-current resize-none"
              style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)", color: "var(--tenant-fg)", fontFamily: "var(--tenant-sans)" }} />
          </div>
          <button type="submit" disabled={sending} data-testid="brutalist-contact-submit"
            className="inline-flex items-center gap-2 px-7 py-3 text-[10px] uppercase tracking-[0.32em] font-bold disabled:opacity-60"
            style={{ background: "var(--tenant-accent)", color: "var(--tenant-bg)" }}>
            {sending ? "TRANSMITTING..." : "$ SUBMIT [→]"}
          </button>
        </form>
      </div>
    </div>
  );
}
